import type { ReactNode } from "react";

import { IntentBadge } from "../status-badge";

/**
 * FunnelRegister — one panel of the Measured Business two-register cross-property
 * funnel (#1186 / #1202). The funnel is shown in two deliberately distinct
 * registers, and that encoding is the whole point of the visual:
 *
 *  - `aggregate` — an anonymous RATE (`exact:false`). Dashed/muted border, because
 *    it is not joined to identities: a proportion, not a count of known parties.
 *  - `exact`     — a count by shared identity (`exact:true`). Solid border.
 *
 * The `register` prop drives the dashed-vs-solid border *and* the IntentBadge, so
 * the encoding lives in one named primitive instead of being hand-rolled per
 * funnel surface (it was two copy-pasted `rounded-md border …` divs in
 * portfolio-rollup.tsx — CLAUDE.md frontend rule #5). These panels are NOT cards
 * (they must not carry card elevation) and are not list rows, so neither `<Card>`
 * nor the list-row pattern fits — this small block is the right home for the idiom.
 *
 * Presentational + server-safe (no hooks). Pass the stat line as children; it is
 * laid out as a baseline-aligned flex row to match the existing funnel rows.
 */
export function FunnelRegister({
  register,
  caption,
  children,
}: {
  register: "aggregate" | "exact";
  caption: string;
  children: ReactNode;
}) {
  const aggregate = register === "aggregate";
  return (
    <div
      className={
        aggregate
          ? "rounded-md border border-dashed border-border/60 bg-muted/30 p-3"
          : "rounded-md border border-border p-3"
      }
    >
      <div className="mb-1 flex items-center gap-2">
        <IntentBadge intent={aggregate ? "neutral" : "success"}>
          {aggregate ? "aggregate rate" : "exact"}
        </IntentBadge>
        <span className="type-subtext text-muted-foreground">{caption}</span>
      </div>
      <div className="flex items-baseline gap-2 type-body">{children}</div>
    </div>
  );
}
